import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import { Hero } from "../components/site/Hero";
import { Marquee } from "../components/site/Marquee";
import { Reveal, MaskLines } from "../components/site/Reveal";
import { CtaBand } from "../components/site/Sections";
import { CaseCard } from "../components/site/CaseCard";
import { ServiceIcon } from "../components/site/ServiceIcon";
import { Seo } from "../components/site/Seo";
import { SERVICES, TESTIMONIALS } from "../data/site";
import { useCases } from "../lib/useCases";

const BENTO = [
  { span: "md:col-span-4 md:row-span-2", tone: "bg-navy text-white", big: true },
  { span: "md:col-span-2", tone: "bg-white text-ink" },
  { span: "md:col-span-2", tone: "bg-white text-ink" },
  { span: "md:col-span-2", tone: "bg-white text-ink" },
  { span: "md:col-span-2", tone: "bg-white text-ink" },
  { span: "md:col-span-2", tone: "bg-flame text-white" },
];

const ServiceTile = ({ s, b, i }) => (
  <Reveal delay={i * 0.06} className={`${b.span} h-full`}>
    <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}
      className={`group relative flex h-full min-h-[220px] flex-col justify-between overflow-hidden rounded-[1.5rem] border border-ink/[0.06] p-6 transition-transform duration-500 hover:-translate-y-1 sm:p-8 ${b.tone}`}>
      {b.big && <img src="/img/workshop.webp" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity transition-transform duration-[1.2s] group-hover:scale-105" />}
      <div className="relative flex items-start justify-between">
        <span className={`grid h-11 w-11 place-items-center rounded-xl ${b.tone.includes("bg-white") ? "bg-ink text-canvas" : "bg-white/15 text-white"}`}>
          <ServiceIcon name={s.icon} />
        </span>
        <ArrowUpRight className="h-6 w-6 transition-transform duration-500 group-hover:rotate-45" />
      </div>
      <div className="relative mt-10">
        <h3 className={`font-display font-bold tracking-tight ${b.big ? "text-4xl sm:text-5xl" : "text-2xl"}`}>{s.title}</h3>
        <p className={`mt-3 text-sm leading-relaxed ${b.tone.includes("bg-white") ? "text-ink/70" : "text-white/85"} ${b.big ? "max-w-md md:text-base" : ""}`}>{s.short}</p>
      </div>
    </Link>
  </Reveal>
);

const Intro = () => (
  <section className="wrap section">
    <div className="grid gap-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-3"><span className="eyebrow">(01) The short version</span></Reveal>
      <div className="lg:col-span-9">
        <MaskLines as="h2" onLoad={false} className="font-display text-3xl font-semibold leading-[1.12] tracking-tight text-ink sm:text-5xl"
          lines={["Good marketing shouldn't", "need a translator.", <span key="x" className="text-ink/45">Ours doesn't.</span>]} />
        <Reveal delay={0.2} className="mt-10 grid gap-8 sm:grid-cols-2">
          <p className="lede">We're a small Canberra team that plans, makes and measures marketing for businesses ready to grow. You'll always know what we're doing, why we're doing it, and what it's earning you.</p>
          <p className="lede">One channel done properly, or a whole marketing team on call? Either way, we start with your goals and work backwards. Not the other way round.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesBento = () => (
  <section className="wrap pb-20 sm:pb-28" aria-labelledby="services-heading">
    <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div>
        <Reveal><span className="eyebrow">(02) What we do</span></Reveal>
        <MaskLines as="h2" onLoad={false} lines={["Six ways to grow.", "One team to call."]} className="h2 mt-5" testId="services-heading" />
      </div>
      <Reveal><Link to="/services" className="btn-ghost" data-testid="home-all-services">Explore services <ArrowUpRight className="h-4 w-4" /></Link></Reveal>
    </div>
    <div className="grid auto-rows-[minmax(220px,auto)] grid-cols-1 gap-4 md:grid-cols-6 md:gap-5">
      {SERVICES.map((s, i) => <ServiceTile key={s.slug} s={s} b={BENTO[i]} i={i} />)}
    </div>
  </section>
);

const Results = () => {
  const { cases } = useCases();
  return (
  <section className="bg-white py-20 sm:py-28 lg:py-36">
    <div className="wrap">
      <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Reveal><span className="eyebrow">(03) Client results</span></Reveal>
          <MaskLines as="h2" onLoad={false} lines={["Numbers don't", "do small talk."]} className="h2 mt-5" />
        </div>
        <Reveal><Link to="/work" className="btn-ghost" data-testid="home-all-work">All the proof <ArrowUpRight className="h-4 w-4" /></Link></Reveal>
      </div>
      <div className="grid gap-12 md:grid-cols-2 md:gap-8">
        {cases.slice(0, 2).map((c, i) => <Reveal key={c.slug} delay={i * 0.1} className={i === 1 ? "md:mt-24" : ""}><CaseCard c={c} index={i} compact /></Reveal>)}
      </div>
    </div>
  </section>
  );
};

const Testimonials = () => (
  <section className="wrap section" aria-labelledby="testimonials-heading">
    <Reveal><span className="eyebrow">(04) Said about us</span></Reveal>
    <MaskLines as="h2" onLoad={false} lines={["Their words, not ours."]} className="h2 mt-5" testId="testimonials-heading" />
    <div className="mt-14 grid gap-5 md:grid-cols-3">
      {TESTIMONIALS.map((t, i) => (
        <Reveal key={i} delay={i * 0.1} className="h-full">
          <figure className={`flex h-full flex-col justify-between rounded-[1.5rem] p-8 ${i === 1 ? "bg-ink text-canvas" : "border border-ink/10 bg-white text-ink"}`} data-testid={`testimonial-${i}`}>
            <Quote className="h-8 w-8 text-flame" />
            <blockquote className="mt-6 font-display text-xl font-medium leading-snug tracking-tight">“{t.quote}”</blockquote>
            <figcaption className={`mt-8 border-t pt-5 text-sm ${i === 1 ? "border-canvas/15 text-neutral-300" : "border-ink/10 text-ink/70"}`}>
              <span className={`block font-semibold ${i === 1 ? "text-canvas" : "text-ink"}`}>{t.name}</span>{t.role}
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo path="/" description="The Catalyst is a Canberra marketing agency. We create momentum for ambitious brands with strategy, content, SEO, social, email and paid campaigns." />
      <Hero />
      <Marquee />
      <Intro />
      <ServicesBento />
      <Results />
      <Testimonials />
      <CtaBand />
    </>
  );
}
